/**
 * Shared multi-LE detection and filtering utilities.
 *
 * Used by:
 * - UnifiedEditionModal (edition flows) — employees are pre-selected
 * - Future: updater/import flows — employees are resolved from CSV mapping
 */

import type { FieldSelectionField } from 'modules/universal_importer/components/FieldSelectionModal'

// --- Types ---

export interface EmployeeWithLe {
  id: string
  legalEntityId: string | null
}

export interface LegalEntityWithCountry {
  id: string
  country: string
}

// --- Detection ---

/**
 * Returns true when the given employees belong to more than one legal entity.
 */
export function detectIsMultiLe(employees: EmployeeWithLe[]): boolean {
  const leIds = new Set<string>()
  for (const emp of employees) {
    if (emp.legalEntityId) {
      leIds.add(emp.legalEntityId)
      if (leIds.size > 1) return true
    }
  }
  return false
}

/**
 * Returns the distinct set of LE IDs from the given employees.
 */
export function getDistinctLeIds(employees: EmployeeWithLe[]): string[] {
  const leIds = new Set<string>()
  for (const emp of employees) {
    if (emp.legalEntityId) leIds.add(emp.legalEntityId)
  }
  return Array.from(leIds)
}

// --- Field filtering ---

/**
 * Given a set of selected field keys and multi-LE state, removes LE-dependent
 * fields from the selection when isMultiLe is true.
 */
export function filterLeDependentFieldKeys(
  selectedKeys: Set<string>,
  fields: FieldSelectionField[],
  isMultiLe: boolean
): Set<string> {
  if (!isMultiLe) return selectedKeys
  const next = new Set(selectedKeys)
  for (const f of fields) {
    if (f.leDependant) next.delete(f.key)
  }
  return next
}

// --- Metadata builders ---

/**
 * Builds a LE id → country lookup from a list of legal entities.
 */
export function buildLegalEntityCountryMap(
  legalEntities: LegalEntityWithCountry[]
): Record<string, string> {
  const map: Record<string, string> = {}
  for (const le of legalEntities) {
    map[le.id] = le.country
  }
  return map
}

/**
 * Builds the metadata object for NuvoImporter from the modal result.
 *
 * - Single LE: `legal_entity_ids = [leId]`, `legal_entity_countries = [country]`
 * - Multi LE: both arrays are empty (LE-dependent fields are already filtered out)
 */
export function buildNuvoMetadata(
  leIds: string[] | null,
  countryMap: Record<string, string>,
  employeeIds: string[]
): {
  legal_entity_ids: bigint[]
  legal_entity_countries: string[]
  employee_ids: bigint[]
  legal_entity_country_map: string[]
} {
  const legalEntityIds = leIds ? leIds.map(BigInt) : []
  const legalEntityCountries = leIds ? leIds.map((id) => countryMap[id]).filter(Boolean) : []

  return {
    legal_entity_ids: legalEntityIds,
    legal_entity_countries: legalEntityCountries,
    employee_ids: employeeIds.map(BigInt),
    legal_entity_country_map: [JSON.stringify(countryMap)],
  }
}
